import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { DTypeFormControl } from '@grupo-constant/next-dtype/form';
import { TranslocoService } from '@ngneat/transloco';
import { firstValueFrom } from 'rxjs';
import { MultiFileUploader } from './multifile-uploader';
import { FuseConfirmationService } from '../../../../@fuse/services/confirmation/confirmation.service';
import { DialogsService } from '../../../dialogs/dialogs.service';
import { HttpService } from '../../http/http.service';
import { FileService } from '../../file/file.service';

@Component({
    selector: 'multifile-uploader-component',
    templateUrl: './multifile-uploader.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiFileUploaderComponent extends MultiFileUploader implements OnInit {

    @Input() acceptedExtensions: string[] = [];
    @Input() control: DTypeFormControl; // required
    @Input() filePattern: RegExp;
    @Input() caption: string;
    @Input() embedded = true;
    @Input() min: number;
    @Input() max: number;
    @Input() extraPath: string = '';
    @Input() isComponentImage = true;

    previewedFile: {
        type: string;
        filename: string;
        base64: string;
        imageUrl: string;
        isImage: boolean;
        serverName: string;
    };

    printedFiles: {
        type: string;
        filename: string;
        base64: string;
        imageUrl: string;
        isImage: boolean;
        serverName: string;
    }[] = [];
    fileNames: string[] = [];
    loading = false;
    maxError = false;
    minError = false;

    dragOverCounter = 0;
    isDragOver = false;

    constructor(
        private fileService: FileService,
        private httpService: HttpService,
        private dialogService: DialogsService,
        private tr: TranslocoService,
        private _fuseConfirmationService: FuseConfirmationService,
    ) {
        super();
    }

    async ngOnInit() {
        if (!this.control.value) {
            this.control.setValue([], { emitEvent: false });
        }
        this.fileNames = this.control.value;
        this.minError = this.fileNames.length < this.min;
        if (this.fileNames) {
            let _filename = '';
            let _base64 = '';
            let _type = '';
            let _isImage = false;
            let _imageUrl = '';
            for (const value of this.fileNames) {
                // value wanted: 'file_name.example'
                _filename = value // 'aaa/bbb/1234_file_name.example.bin'
                    .split('/')       // ['aaa', 'bbb', '1234_file_name.example.bin']
                    .slice(-1)[0]     // '1234_file_name.example.bin'
                    .split('_')       // ['1234', 'file', 'name.example.bin']
                    .slice(1)         // ['file', 'name.example.bin']
                    .join('_');        // 'file_name.example.bin'
                //.split('.')       // ['file_name', 'example', 'bin']
                //.slice(0, -1)     // ['file_name', 'example']
                //.join('.');       // 'file_name.example'

                if (!!value && this.isComponentImage) {
                    if (value.startsWith('data:')) {
                        _base64 = value;
                        const mimetype = this.fileService.getFileType(_base64);
                        if (mimetype) {
                            _type = mimetype[1];
                            if (mimetype[0].startsWith('image')) {
                                _isImage = true;
                            }
                        }

                    } else if (value.startsWith(':')) {
                        this.loading = true;
                        try {
                            const file = await this.fileService.download(value);
                            if (file) {
                                _base64 = file;
                                const mimetype = this.fileService.getFileType(_base64);
                                if (mimetype) {
                                    _type = mimetype[1];
                                    if (mimetype[0].startsWith('image')) {
                                        _isImage = true;
                                    }
                                }
                            }
                        } finally {
                            this.loading = false;
                        }
                    } else {
                        _isImage = true;
                        _filename = undefined;
                        _imageUrl = value.startsWith('http') ? value : `${this.httpService.getBaseUrl()}${value}`;
                    }
                }
                this.printedFiles.push({
                    type: _type,
                    filename: _filename,
                    base64: _base64,
                    imageUrl: _imageUrl,
                    isImage: _isImage,
                    serverName: value
                });
            }
        }
    }

    uploadFile(file: File) {
        if (this.control.disabled) {
            return;
        }
        if (file.size === 0) {
            this.emptyFileDialog();
        } else {
            if (this.fileNames.length < this.max || !this.max) {
                this.loading = true;
                const split = file.name.split('.');
                const ext = split[split.length - 1];
                const filename = split.slice(0, split.length - 1).join('.');
                if (this.acceptedExtensions.length > 0 && !this.acceptedExtensions.map(ex => ex.toLowerCase()).includes(ext.toLowerCase())) {
                    this.dialogService.openErrorDialog({
                        title: this.tr.translate('invalidFormat'),
                        message: this.tr.translate('invalidExtension'),
                    });
                    this.loading = false;
                } else if (this.filePattern && !this.filePattern.test(filename)) {
                    this.dialogService.openErrorDialog({
                        title: this.tr.translate('invalidFormat'),
                        message: this.tr.translate('invalidRegex'),
                    });
                    this.loading = false;
                } else {
                    const reader = new FileReader();
                    let _serverName: string;
                    let _isImage: boolean;
                    reader.onload = async (e) => {
                        if (file.type.split('/')[0].startsWith('image')) {
                            _isImage = true;
                        } else {
                            _isImage = false;
                        }
                        const _type = ext;
                        const _filename = file.name;
                        const _imageUrl = _filename.startsWith('http') ? _filename : `${this.httpService.getBaseUrl()}${_filename}`;
                        const _base64 = String((e.target as FileReader).result);
                        try {
                            if (this.embedded) {
                                this.fileNames.push(_base64);
                                _serverName = _base64;
                            } else {
                                _serverName = await firstValueFrom(this.fileService.upload(file.name, _base64, this.extraPath));
                                this.fileNames.push(_serverName);
                            }
                            this.control.setValue(this.fileNames);
                            if (this.isComponentImage) {
                                this.printedFiles.push({
                                    type: _type,
                                    filename: _filename,
                                    base64: _base64,
                                    imageUrl: _imageUrl,
                                    isImage: _isImage,
                                    serverName: _serverName
                                });
                            } else {
                                this.printedFiles.push({
                                    type: '',
                                    filename: _filename,
                                    base64: '',
                                    imageUrl: '',
                                    isImage: false,
                                    serverName: _serverName
                                });
                            }
                        } catch (error) {
                            console.error(error);
                        } finally {
                            this.loading = false;
                            this.minError = this.fileNames.length < this.min;
                        }
                    };

                    reader.onerror = () => this.loading = false;

                    reader.readAsDataURL(file);
                }
            } else {
                this.maxError = true;
            }
        }
    }

    async downloadFile(event: MouseEvent, file: any): Promise<void> {
        event.stopPropagation();
        const confirmed = await this.confirm('downloadFileConfirm');
        if (confirmed) {
            const e = document.createElement('a');
            if (this.isComponentImage) {
                e.href = file.imageUrl || file.base64;
            } else {
                const tempFile = await this.fileService.download(file.serverName);
                if (tempFile) {
                    e.href = tempFile;
                }
            }
            e.target = file.imageUrl ? '_blank' : e.target;
            e.download = file.filename || 'unnamed_file';
            e.click();
        }
    }

    async removeFile(event: MouseEvent, file: any): Promise<void> {
        event.stopPropagation();
        const confirmed = await this.confirm('removeFileConfirm');
        if (confirmed) {
            const index = this.printedFiles.findIndex(item => item.serverName === file.serverName);
            if (index > -1) {
                this.printedFiles.splice(index, 1);
            }
            const index2 = this.fileNames.findIndex(item => item === file.serverName);
            if (index2 > -1) {
                this.fileNames.splice(index2, 1);
            }
            this.control.setValue(this.fileNames);
            this.maxError = false;
            this.minError = this.fileNames.length < this.min;
        }
    }

    previewFile(event: MouseEvent, i: number) {
        event.stopPropagation();
        this.previewedFile = this.printedFiles[i];
        const image = new Image();
        image.src = this.previewedFile.base64;
        this.dialogService.openPreviewFileDialog(this.previewedFile,  '80vw', '80vh');
    }

    getDialogWidth(width: number) {
        if (width > 1800) {
            return '90vw';
        } else if (width > 1500) {
            return '80vw';
        } else if (width > 1200) {
            return '70vw';
        } else if (width > 900) {
            return '60vw';
        } else if (width > 600) {
            return '50vw';
        } else if (width > 300) {
            return '40vw';
        } else if (width > 100) {
            return '30vw';
        } else {
            return '20vw';
        }
    }

    getDialogHeight(height: number) {
        if (height > 900) {
            return '90vh';
        } else if (height > 700) {
            return '80vh';
        } else if (height > 500) {
            return '70vh';
        } else if (height > 300) {
            return '60vh';
        } else if (height > 100) {
            return '35vh';
        } else {
            return '20vh';
        }
    }

    getFileWitdh(file: any) {
        const image = new Image();
        image.src = file.base64;
        return image.width;
    }

    getFileHeight(file: any) {
        const image = new Image();
        image.src = file.base64;
        return image.height;
    }

    private confirm(_message: string) {
        const dialogRef = this._fuseConfirmationService.open({
            title: this.tr.translate('warning'),
            message: this.tr.translate(_message),
            actions: {
                confirm: {
                    label: this.tr.translate('accept')
                },
                cancel: {
                    label: this.tr.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe(result => resolve(result === 'confirmed'));
        });
    }

    private emptyFileDialog() {
        this._fuseConfirmationService.open({
            title: this.tr.translate('warning'),
            message: this.tr.translate('emptyFileDialog'),
            actions: {
                confirm: {
                    label: this.tr.translate('accept')
                },
                cancel: {
                    show: false,
                }
            }
        });
    }

}
